import React from 'react';
import { Scrollbars } from 'rc-scrollbars';
import classnames from 'classnames/bind';
import classNames from 'classnames';
import { Portal } from 'react-portal';
import moment from 'moment';
import { debounce } from 'lodash-es';
import { useIntl, useParams } from 'umi';
import { inject, observer } from 'mobx-react';
import { ChatMessage, MemberStruct, ParamsStruct, ChatMessageObjectCustom } from '@/types/meeting';
import UserModel from '@/models/user';
import MeetingModal from '@/models/meeting';
import Chat from '@/models/chat';
import RTCInstance from '@/models/rtc';
import { Avatar, Icon } from '@/components';
import Style from './index.less';
import ICON_CLOSE from '@/assets/icon-close.png';
import ICON_SEND_DEFAULT from '@/assets/icon-send-default.png';
import ICON_SEND_HIGHLIGHT from '@/assets/icon-send-highlight.png';
import NickName from '@/components/NickName';
import DOMPurify from 'dompurify';

interface ChatPanelProps {
  visible: boolean;
  talkingIds: string[];
  meeting?: MeetingModal;
  chat?: Chat;
  user?: UserModel;
  rtc?: RTCInstance;
}

const $c = classnames.bind(Style);

function insertBr(text = ''): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  return escaped.replace(/\n/g, '<br/>');
}

const sanitizeText = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const ChatPanel: React.FC<ChatPanelProps> = inject(({ store }) => {
  return {
    meeting: store.meeting,
    user: store.user,
    chat: store.chat,
    rtc: store.rtc,
  };
})(
  observer((props: ChatPanelProps) => {
    const { meetingId } = useParams<ParamsStruct>();
    const { visible, meeting, chat, rtc } = props;
    const [inputText, setInputText] = React.useState<string>('');
    const [isScrolling, setScrolling] = React.useState<boolean>(false);
    const [isToBottom, setIsToBottom] = React.useState<boolean>(false);
    const [moreHeight, setMoreHeight] = React.useState(false);
    const [showChatFullscreenInput, setShowChatFullscreenInput] = React.useState(false);
    const chatFullscreenInputTextarea = React.useRef<HTMLTextAreaElement>(null);
    const { formatMessage } = useIntl();
    const $i = (mid: string) => {
      return formatMessage({ id: mid });
    };
    const renderThumb = ({ style, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
      const thumbStyle = {
        backgroundColor: `rgba(255, 255, 255, 0.5)`,
        borderRadius: '3px',
        width: '6px',
      };
      return <div style={{ ...style, ...thumbStyle }} {...props} />;
    };
    const scrollDom = React.useRef<Scrollbars>(null);

    const undateScrolling = React.useCallback(() => {
      if (isToBottom) return;
      const sroller = scrollDom.current;
      if (!moreHeight) {
        setMoreHeight(sroller!.getScrollHeight() > sroller!.getClientHeight());
      }
      const isSroll =
        sroller!.getScrollHeight() - sroller!.getScrollTop() - sroller!.getClientHeight() > 20;
      if (!isSroll) {
        chat?.setNewMsg(false);
      }
      setScrolling(() => isSroll);
    }, [chat, isToBottom, moreHeight]);

    const scrollByMsgId = React.useCallback((msgId: string, highlight = true) => {
      const msgDom: HTMLElement | null = document.querySelector(`[data-msgid="${msgId}"]`);
      if (msgDom) {
        scrollDom.current?.scrollTop?.(msgDom.offsetTop);
        if (highlight) {
          const lname = ` ${$c('locate-item')}`;
          msgDom.className += lname;
          setTimeout(() => {
            msgDom.className = msgDom.className.replace(lname, '');
          }, 2000);
        }
      }
    }, []);

    const onScroll = debounce(function onScroll(e: any) {
      undateScrolling();
      if (!(chat?.noMore || chat?.loading)) {
        const oldHeight = e.scrollHeight;
        const oldTop = e.scrollTop;
        if (oldTop < 100) {
          chat?.getHistoryMessage(0, 30).then((data: any) => {
            if (data?.msgs?.length) {
              scrollDom.current?.scrollTop(
                scrollDom.current?.getScrollHeight() - oldHeight + oldTop
              );
              undateScrolling();
            }
          });
        }
      } else {
        undateScrolling();
      }
    }, 200);

    const initChatRoom = React.useCallback(async () => {
      if (!rtc?.cid) {
        return;
      }
      let i = 0;
      let err: any;
      const times = 3;
      for (; i < times; i++) {
        try {
          await chat?.chatInit(meetingId);
          break;
        } catch (e) {
          err = e;
          if (i + 1 < times) {
            // eslint-disable-next-line no-loop-func
            await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** i));
          }
        }
      }
      if (i === times) {
        console.error('failed to init chat', err);
        return;
      }
      try {
        const data = await chat!.getHistoryMessage(0, 30);
        if (data?.msgs?.length) {
          scrollDom.current?.scrollToBottom();
        }
      } catch (e) {
        console.error('failed to get history message', e);
      }
    }, [chat, chat?.isAbleUse, meetingId, rtc?.cid, scrollDom.current]);

    const reconnectChatRoom = React.useCallback(async () => {
      if (!rtc?.cid) {
        return;
      }
      if (chat?.disconnected) {
        let i = 0;
        let err: any;
        const times = 3;
        for (; i < times; i++) {
          try {
            await chat?.chatInit(meetingId);
            break;
          } catch (e) {
            err = e;
            if (i + 1 < times) {
              // eslint-disable-next-line no-loop-func
              await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** i));
            }
          }
        }
        if (i === times) {
          console.error('failed to init chat', err);
          return;
        }
      }
      if (chat?.disconnected) {
        return;
      }
      try {
        const data = await chat!.resetHistoryMessage();
        if (data?.msgs?.length) {
          scrollDom.current?.scrollToBottom();
        }
      } catch (e) {
        console.error('failed to get history message', e);
      }
    }, [chat, chat?.disconnected, chat?.isAbleUse, meetingId, rtc?.cid, scrollDom.current]);

    React.useEffect(() => {
      initChatRoom();
    }, [chat, chat?.isAbleUse, meetingId, rtc?.cid]);

    React.useEffect(() => {
      reconnectChatRoom();
    }, [chat?.reconnected]);

    const scrollPopoToBottom = React.useCallback(() => {
      setIsToBottom(true);
      chat?.setNewMsg(false);
      const toBottom =
        scrollDom!.current!.getScrollHeight() -
        scrollDom.current!.getScrollTop() -
        scrollDom.current!.getClientHeight();
      if (toBottom > 0) {
        window.requestAnimationFrame(scrollPopoToBottom);
        scrollDom.current?.scrollTop(scrollDom.current?.getScrollTop() + Math.ceil(toBottom / 8));
      } else {
        setIsToBottom(false);
        undateScrolling();
      }
    }, [chat]);

    const closeChatPanel = React.useCallback(() => {
      if (!chat?.isAbleUse) {
        return;
      }
      chat?.setChatVisible(false, true);
    }, [chat]);

    const toggleChatFullscreenInput = React.useCallback(() => {
      setShowChatFullscreenInput((pre) => {
        if (!pre) {
          chatFullscreenInputTextarea.current?.focus();
        }
        return !pre;
      });
    }, []);

    const sendChatFullscreenText = React.useCallback(() => {
      const trimmedText = inputText.trim();
      if (trimmedText) {
        chat?.sendText(trimmedText);
        setInputText('');
        toggleChatFullscreenInput();
      }
    }, [chat, inputText, toggleChatFullscreenInput]);

    React.useEffect(
      debounce(() => {
        if (chat?.chatVisible && (isToBottom || !isScrolling)) {
          scrollPopoToBottom();
        } else if (chat?.chatVisible) {
          chat?.setNewMsg(true);
        }
      }, 200),
      [chat?.lastMsgId]
    );

    React.useEffect(() => {
      if (chat?.firstUnreadMsg) {
        scrollByMsgId(chat.firstUnreadMsg.idClient, false);
      } else if (chat?.unReadMsg) {
        scrollByMsgId(chat.unReadMsg.idClient);
      } else {
        scrollPopoToBottom();
      }
    }, [chat, chat?.chatVisible]);

    const inputKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
      const trimmedText = inputText.trim();
      if (!trimmedText) {
        setInputText('');
        return;
      }
      if (!window.isMobile && e.keyCode === 13) {
        if (e.shiftKey || e.ctrlKey) {
          setInputText(`${inputText}\n`);
        } else {
          e.preventDefault();
          scrollDom?.current?.scrollToBottom();
          undateScrolling();
          // 发送前清理文本，确保不包含恶意代码
          chat?.sendText(trimmedText);
          setInputText('');
        }
      }
    };

    const inputChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      if (window.isMobile) {
        setInputText(e.target.value);
      } else if ((e.nativeEvent as InputEvent).inputType !== 'insertLineBreak') {
        setInputText(e.target.value);
      }
    };

    return (
      <div
        className={classNames(Style['chat-panel'], {
          hidden: !visible,
        })}
      >
        <div className={$c('chat-title')}>
          {window.isMobile ? (
            <>
              <img
                src={ICON_CLOSE}
                className={$c('chat-title-icon-close')}
                alt=""
                onClick={closeChatPanel}
              />
              <div className={$c('chat-title-main')}>{$i('CHAT')}</div>
              <div className={$c('chat-title-meeting-name')}>{meeting?.title}</div>
            </>
          ) : (
            <>
              {$i('CHAT')}
              <div
                onClick={scrollPopoToBottom}
                className={$c('newmsg-jump', { hide: !chat?.hasNewMsg })}
              >
                {$i('NEW_NEWS')}↓
              </div>
            </>
          )}
        </div>
        <div className={$c('chat-list')}>
          <Scrollbars renderThumbVertical={renderThumb} ref={scrollDom} onScrollFrame={onScroll}>
            <div className={Style['panel-wrapper']}>
              <div className={$c('loading-box', { hide: chat?.noMore })}>
                <Icon name="icon-loading_w_nor" className={$c('loading-icon')} />
                {$i('LOADING')}...
              </div>
              <div className={$c('loading-box', { hide: !(moreHeight && chat?.noMore) })}>
                {$i('NO_MORE_NEWS')}
              </div>
              {chat?.getMessageList?.map((message) => {
                const msg = message as ChatMessage;
                const member = meeting?.getMemberInfoByChatId(msg.from) as MemberStruct;
                return (
                  <div
                    key={msg.idClient}
                    // className={$c('msg-item', { 'msg-my': msg.flow === 'out' })}
                    className={$c('msg-item')}
                    data-msgid={msg.idClient}
                  >
                    <div className={$c('msg-left')}>
                      <Avatar
                        size={isMobile ? 32 : 28}
                        type="cover"
                        avatarId={0}
                        yxuid={(msg.custom as ChatMessageObjectCustom).yxUid}
                        name={msg.fromNick || member?.memberName || ''}
                      />
                    </div>
                    <div className={$c('msg-right')}>
                      <div className={$c('msg-header')}>
                        <span className={$c('nick-name')}>
                          <NickName
                            yxUid={(msg.custom as ChatMessageObjectCustom).yxUid}
                            defaultName={msg.fromNick || member?.memberName || ''}
                          />
                        </span>
                        {(msg.custom as ChatMessageObjectCustom).yxUid === meeting?.hostYxUid && (
                          <span className={$c('host-tag')}>（{$i('HOST')}）</span>
                        )}
                        <span className={$c({ hide: msg.flow !== 'out' }, 'me-tag')}>
                          （{$i('ME')}）
                        </span>
                        <span className={$c('msg-time')}>{moment(msg.time).format('H:mm:ss')}</span>
                      </div>
                      <div className={$c('msg-content')}>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(insertBr(msg.text), {
                              ALLOWED_TAGS: ['br'],
                            }),
                          }}
                        />
                        <span
                          className={$c('after-icon', 'delay-show', {
                            hide: msg.status !== 'sending',
                          })}
                        >
                          <Icon
                            name="icon-loading_w_nor"
                            className={$c('msg-icon', 'loading-icon')}
                          />
                        </span>
                        <Icon
                          name="iconexclamation_solid"
                          style={{ color: '#FF5762' }}
                          className={$c('after-icon', 'msg-icon', {
                            hide: ['sending', 'success'].includes(msg.status),
                          })}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Scrollbars>
        </div>
        <div className={$c('chat-input')}>
          {window.isMobile ? (
            <div className={$c('chat-input-placeholder')} onClick={toggleChatFullscreenInput}>
              {inputText ? sanitizeText(inputText) : $i('SEND_A_MESSAGE_TO_EVERYONE')}
            </div>
          ) : (
            <>
              <textarea
                placeholder={$i('SEND_A_MESSAGE_TO_EVERYONE')}
                onKeyDown={inputKeyDown}
                onChange={inputChange}
                value={inputText}
                maxLength={4096}
              />
              <div className={$c('bottom')}>
                <span className={$c('text-length')}>{inputText.length}/4096</span>
                <Icon name="icon-enter" className={$c('send-icon')} />
                {$i('SEND')} <span className={$c('send-icon')}>/</span>{' '}
                <span className={$c('send-icon')}>Ctrl</span>
                <Icon name="icon-enter" className={$c('send-icon')} /> {$i('LINE_FEED')}
              </div>
            </>
          )}
        </div>
        {isMobile && (
          <Portal>
            <div
              className={$c('chat-fullscreen-input-background')}
              style={{
                left: showChatFullscreenInput ? 0 : 9999,
              }}
            />
            <div
              className={$c('chat-fullscreen-input')}
              style={{ left: showChatFullscreenInput ? 0 : 9999 }}
            >
              <div className={$c('chat-fullscreen-input-header')}>
                <img
                  className={$c('chat-fullscreen-input-header-icon-close')}
                  src={ICON_CLOSE}
                  alt=""
                  onClick={toggleChatFullscreenInput}
                />
                <img
                  className={$c('chat-fullscreen-input-header-icon-send')}
                  src={inputText ? ICON_SEND_HIGHLIGHT : ICON_SEND_DEFAULT}
                  alt=""
                  onClick={sendChatFullscreenText}
                />
              </div>
              <textarea
                ref={chatFullscreenInputTextarea}
                placeholder={$i('SEND_A_MESSAGE_TO_EVERYONE')}
                onKeyDown={inputKeyDown}
                onChange={inputChange}
                value={inputText}
                maxLength={4096}
                enterkeyhint="enter"
              />
            </div>
          </Portal>
        )}
      </div>
    );
  })
);

export default ChatPanel;
